import {AppLocale} from '@config/locales';
import {UrlParamsKeys} from '@config/urlParamsKeys';
import {getPrependLocale} from '@utils/prependLocale';
import queryString from 'query-string';

const buildFilterUrl = <T extends object>(
    base: string,
    filter: T,
    locale?: AppLocale,
) => {
    const query = queryString.stringify(filter as Record<string, unknown>, {
        skipNull: true,
        skipEmptyString: true,
        arrayFormat: 'comma',
    });
    const route = query ? `${base}?${query}` : base;
    return locale ? getPrependLocale(route, locale) : route;
};

export const Route = {
    HOME: '/',
    NOT_FOUND: '/not-found',
    MAINTENANCE: '/maintenance',
    ONBOARDING_CATEGORY: '/onboarding/category',
    ONBOARDING_SUBCATEGORY: '/onboarding/subcategory',
    ONBOARDING_GOAL: '/onboarding/goal',
    ONBOARDING_EXPERIENCE: '/onboarding/experience',
    ONBOARDING_TRAINERS: '/onboarding/trainer',
    ONBOARDING_USER_TYPE: '/onboarding/user-type',
    ONBOARDING_SURVEY: '/onboarding/survey',
    ONBOARDING_SELECTION: '/onboarding/selection',
    ONBOARDING_SELECTION_COURSE_LESSON: (
        slug: string,
        id: string,
        lessonId: string,
        isBestChoice?: boolean,
    ) =>
        queryString.stringifyUrl({
            url: `/onboarding/selection/${slug}/${id}/${lessonId}`,
            query: {isBestChoice: isBestChoice ? 'true' : null},
        }),
    ONBOARDING_STEP: (step: string, existingUser?: boolean) =>
        queryString.stringifyUrl(
            {
                url: step,
                query: {
                    [UrlParamsKeys.EXISTING_USER]: existingUser ? 'true' : null,
                },
            },
            {skipNull: true, skipEmptyString: true},
        ),

    COURSES: '/courses',
    COURSES_FILTERED: <T extends object>(filter: T, locale?: AppLocale) =>
        buildFilterUrl(Route.COURSES, filter, locale),
    COURSE: (slug: string, module?: string, scrollin?: boolean) => {
        if (!module && !scrollin) {
            return `/courses/${slug}`;
        }

        return queryString.stringifyUrl(
            {
                url: `/courses/${slug}`,
                query: {
                    module,
                    scrollin: scrollin ? 'true' : undefined,
                },
            },
            {skipNull: true, skipEmptyString: true},
        );
    },
    COURSE_RECOMMENDATIONS: (slug: string, scrollin?: boolean) =>
        queryString.stringifyUrl({
            url: `/courses/${slug}/recommendations`,
            query: {
                scrollin,
            },
        }),
    COURSE_ITEM: ({
        slug,
        id,
        autoplay,
    }: {
        slug: string;
        id: number;
        autoplay?: number;
    }) =>
        queryString.stringifyUrl(
            {
                url: `/courses/${slug}/${id}`,
                query: {
                    autoplay,
                },
            },
            {skipNull: true, skipEmptyString: true},
        ),
    COURSE_LESSONS: (slug: string, moduleId: string, lessonId: string) =>
        `/courses/${slug}/${moduleId}/${lessonId}`,
    COURSE_NAVIGATOR: (
        slug: string,
        fromLesson?: {moduleId: string; lessonId: string},
    ) =>
        `/courses/${slug}/navigator${fromLesson ? `?m=${fromLesson.moduleId}&l=${fromLesson.lessonId}` : ''}`,
    COURSE_MODULE_COMPLETE: (
        slug: string,
        {lessonId, moduleId}: {moduleId: string; lessonId: string},
    ) => `/courses/${slug}/module-complete?m=${moduleId}&l=${lessonId}`,
    COURSE_COMPLETE: (slug: string) => `/courses/${slug}/course-complete`,
    LOGIN: '/login',
    LOGIN_WITH_REDIRECT: (from: string) => `${Route.LOGIN}?from=${from}`,
    NEW_PASSWORD: '/new-password',
    PASSWORD_RESET: '/password-reset',
    CHECKOUT: '/checkout/register',
    // intentionally NOT using queryString here — encodedParams is a raw
    // base64 string (btoa) containing PII. queryString would URL-encode the '='
    // padding characters to '%3D', which breaks atob() on decode.
    CHECKOUT_WITH_PARAMS: (encodedParams: string) =>
        `${Route.CHECKOUT}?${UrlParamsKeys.CHECKOUT}=${encodedParams}`,

    // paypal routes are redirected by middleware back to
    // Route.CHECKOUT with an appropriate error message
    CHECKOUT_PAYPAL_FAILURE: '/checkout/payment/failure',
    CHECKOUT_PAYPAL_CANCEL: '/checkout/payment/cancel',
    CHECKOUT_PAYPAL_SUCCESS: '/checkout/payment/activate',

    LOGOUT: '/logout',
    LIVE_EVENTS: '/live-events',
    LIVE_EVENTS_FILTERED: <T extends object>(filter: T, locale?: AppLocale) =>
        buildFilterUrl(Route.LIVE_EVENTS, filter, locale),
    LIVE_EVENT: ({
        slug,
        video,
        chapterTimeStamp,
    }: {
        slug: string;
        video?: number;
        chapterTimeStamp?: number;
    }) =>
        queryString.stringifyUrl(
            {
                url: `/live-events/${slug}`,
                query: {
                    [UrlParamsKeys.VIDEO]: video,
                    [UrlParamsKeys.CHAPTER_TIMESTAMP]: chapterTimeStamp,
                },
            },
            {skipNull: true, skipEmptyString: true},
        ),
    MY_LIST: '/courses?onlyFavorites=true',
    SETTINGS: '/settings',
    PAYMENT_HISTORY: '/settings/payment-history',
    ADMIN: '/admin',

    ONLINE_VET: '/online-vet',
    SENTRY_API_ERROR_URL: '/api/sentry-example-api',
    EXCEPTIONALPAGE: '/admin/exceptionalPage',
} as const;
